import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import {
  Upload,
  Download,
  FileSpreadsheet,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ChevronLeft,
  User,
  Users,
  History,
  Trash2,
  ArrowRightLeft,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const LeadImportPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [users, setUsers] = useState([]);
  const [partners, setPartners] = useState([]);
  const [partnerId, setPartnerId] = useState('');
  const [mode, setMode] = useState('single');
  const [singleUserId, setSingleUserId] = useState('');
  const [poolUserIds, setPoolUserIds] = useState([]);
  const [batches, setBatches] = useState([]);
  const [batchAction, setBatchAction] = useState(null); // { type: 'delete'|'reassign', batch }
  const [reassignTargetPartner, setReassignTargetPartner] = useState('');
  const [reassignTargetUser, setReassignTargetUser] = useState('');
  const [batchActionLoading, setBatchActionLoading] = useState(false);
  const [leaveUnassigned, setLeaveUnassigned] = useState(false);
  const [unassignBatch, setUnassignBatch] = useState(false);
  const [defaultSector, setDefaultSector] = useState('');

  const isManager = ['super_admin', 'gestor', 'admin', 'parceiro', 'backoffice', 'chefe_equipa'].includes(user?.role);

  useEffect(() => {
    if (!isManager) {
      setSingleUserId(user?.id || '');
      return;
    }
    Promise.all([
      axios.get(`${API_URL}/leads/assignable-users`),
      axios.get(`${API_URL}/leads/assignable-partners`).catch(() => ({ data: [] })),
    ]).then(([uRes, pRes]) => {
      setUsers(uRes.data || []);
      setPartners(pRes.data || []);
      // Default partner = current user's partner (or first available)
      const myPid = user?.partner_id;
      if (myPid && (pRes.data || []).some(p => p.id === myPid)) {
        setPartnerId(myPid);
      } else if ((pRes.data || []).length > 0) {
        setPartnerId(pRes.data[0].id);
      }
    }).catch(() => toast.error('Erro a carregar lista de comerciais'));
    loadBatches();
  }, [isManager, user?.id, user?.partner_id]);

  const loadBatches = async () => {
    try {
      const r = await axios.get(`${API_URL}/leads/import-batches`);
      setBatches(r.data || []);
    } catch (e) { /* silently ignore */ }
  };

  const confirmBatchAction = async () => {
    if (!batchAction) return;
    setBatchActionLoading(true);
    try {
      if (batchAction.type === 'delete') {
        await axios.delete(`${API_URL}/leads/import-batches/${encodeURIComponent(batchAction.batch.batch_id)}`, {
          params: { force: !!batchAction.force },
        });
        toast.success(`${batchAction.batch.total} leads eliminadas`);
      } else if (batchAction.type === 'reassign') {
        const body = {};
        if (unassignBatch) {
          body.unassign = true;
        } else if (reassignTargetPartner === '__all__') {
          body.assigned_to_partner = batchAction.partner_id || partnerId;
        } else if (reassignTargetUser) {
          body.assigned_to = reassignTargetUser;
        } else {
          toast.error('Escolha um destino'); setBatchActionLoading(false); return;
        }
        const r = await axios.post(`${API_URL}/leads/import-batches/${encodeURIComponent(batchAction.batch.batch_id)}/reassign`, body);
        toast.success(unassignBatch ? `${r.data.reassigned} leads postas por atribuir` : `${r.data.reassigned} leads reatribuídas`);
      }
      setBatchAction(null);
      setReassignTargetPartner('');
      setReassignTargetUser('');
      setUnassignBatch(false);
      await loadBatches();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro');
    } finally {
      setBatchActionLoading(false);
    }
  };

  // Filtra comerciais pelo parceiro escolhido
  const filteredUsers = partnerId
    ? users.filter(u => u.partner_id === partnerId)
    : users;

  // Quando parceiro muda, limpa selecções para evitar inconsistências
  useEffect(() => {
    setSingleUserId('');
    setPoolUserIds([]);
    if (mode === 'single' && filteredUsers.length > 0) {
      setSingleUserId(filteredUsers[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerId]);

  const togglePool = (id) => {
    setPoolUserIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const downloadTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/leads/import/template`, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` },
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'modelo_leads.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Erro ao descarregar modelo');
    }
  };

  const handleImport = async () => {
    if (!file) { toast.error('Selecione um ficheiro'); return; }
    if (!defaultSector) { toast.error('Escolha o Setor / Serviço a aplicar às leads importadas'); return; }
    if (isManager && !leaveUnassigned) {
      if (!partnerId) { toast.error('Escolha um parceiro'); return; }
      if (mode === 'pool' && poolUserIds.length < 2) { toast.error('Escolha pelo menos 2 comerciais para distribuir'); return; }
      if (mode === 'single' && !singleUserId) { toast.error('Escolha um comercial'); return; }
    }

    setImporting(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (defaultSector) fd.append('default_service_interest', defaultSector);
      if (isManager && leaveUnassigned) {
        // Não envia atribuição → leads ficam por atribuir
      } else if (mode === 'all' && partnerId) {
        fd.append('assigned_to_partner', partnerId);
      } else if (mode === 'pool') {
        fd.append('assigned_to_pool', poolUserIds.join(','));
      } else {
        fd.append('assigned_to', singleUserId);
      }
      const res = await axios.post(`${API_URL}/leads/import`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(res.data);
      if (res.data.inserted > 0) toast.success(`${res.data.inserted} leads importadas`);
      else toast.info('Nenhuma lead nova importada');
      loadBatches();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao importar');
    } finally {
      setImporting(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6 max-w-3xl" data-testid="lead-import-page">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/leads/pipeline')}>
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-outfit flex items-center gap-2">
              <Upload className="w-6 h-6 text-[#2D3E6F]" />
              Importar Leads
            </h1>
            <p className="text-slate-500 mt-1 text-sm">
              Carrega um ficheiro Excel ou CSV. Telefones duplicados são ignorados automaticamente.
            </p>
          </div>
        </div>

        {/* Step 1: Template */}
        <Card className="rounded-2xl border-slate-100">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#2D3E6F] text-white text-xs font-bold">1</span>
              Descarrega o modelo
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-600 mb-3">
              Modelo com colunas certas e linha de exemplo. Abre no Excel, copia os teus contactos e grava.
            </p>
            <Button onClick={downloadTemplate} variant="outline" className="rounded-full gap-2" data-testid="download-template-btn">
              <Download className="w-4 h-4" />
              Descarregar modelo Excel
            </Button>
            <div className="mt-4 p-3 bg-slate-50 rounded-lg text-xs text-slate-600 space-y-1">
              <p><strong>Obrigatórias:</strong> Nome, Telefone</p>
              <p><strong>Opcionais:</strong> Email, NIF, Origem, Localidade, Serviço Pretendido, Notas, Data Contacto</p>
            </div>
          </CardContent>
        </Card>

        {/* Step 2: Atribuição (only for managers) */}
        {isManager && (
          <Card className="rounded-2xl border-slate-100">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#2D3E6F] text-white text-xs font-bold">2</span>
                Para que parceiro vão as leads?
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Opção: importar sem atribuir (gestão distribui depois) */}
              <label
                className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-colors ${
                  leaveUnassigned ? 'border-[#2D3E6F] bg-[#2D3E6F]/5' : 'border-slate-200 hover:bg-slate-50'
                }`}
                data-testid="leave-unassigned-toggle"
              >
                <Checkbox checked={leaveUnassigned} onCheckedChange={(v) => setLeaveUnassigned(!!v)} className="mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-slate-800">Não atribuir agora — deixar por atribuir</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    As leads entram sem comercial. Distribui-las depois em <b>Leads Externas</b> (selecionar + “Atribuir Comercial”).
                  </p>
                </div>
              </label>

              {!leaveUnassigned && (
              <>
              {/* STEP 2A: Parceiro */}
              <div>
                <Label className="text-sm">Parceiro</Label>
                <Select value={partnerId} onValueChange={setPartnerId}>
                  <SelectTrigger data-testid="partner-select"><SelectValue placeholder="Escolher parceiro..." /></SelectTrigger>
                  <SelectContent>
                    {partners.map(p => (
                      <SelectItem key={p.id} value={p.id} disabled={p.comerciais_count === 0}>
                        {p.name} {p.comerciais_count > 0 ? `· ${p.comerciais_count} comercia${p.comerciais_count === 1 ? 'l' : 'is'}` : '· sem comerciais'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {partners.length === 0 && (
                  <p className="text-xs text-rose-500 mt-1">Sem parceiros disponíveis.</p>
                )}
              </div>

              {/* STEP 2B: Modo de distribuição dentro do parceiro */}
              {partnerId && (
                <>
                  <div className="border-t pt-4">
                    <Label className="text-sm">Como distribuir dentro deste parceiro?</Label>
                    <div className="grid grid-cols-3 gap-2 p-1 bg-slate-100 rounded-full mt-2" data-testid="mode-toggle">
                      <button
                        type="button"
                        onClick={() => setMode('single')}
                        className={`px-3 py-2 text-xs font-semibold rounded-full transition-all flex items-center justify-center gap-1.5 ${
                          mode === 'single' ? 'bg-white text-[#2D3E6F] shadow' : 'text-slate-500 hover:text-slate-700'
                        }`}
                        data-testid="mode-single"
                      >
                        <User className="w-3.5 h-3.5" />
                        Um comercial
                      </button>
                      <button
                        type="button"
                        onClick={() => setMode('pool')}
                        className={`px-3 py-2 text-xs font-semibold rounded-full transition-all flex items-center justify-center gap-1.5 ${
                          mode === 'pool' ? 'bg-white text-[#2D3E6F] shadow' : 'text-slate-500 hover:text-slate-700'
                        }`}
                        data-testid="mode-pool"
                      >
                        <Users className="w-3.5 h-3.5" />
                        Vários
                      </button>
                      <button
                        type="button"
                        onClick={() => setMode('all')}
                        className={`px-3 py-2 text-xs font-semibold rounded-full transition-all flex items-center justify-center gap-1.5 ${
                          mode === 'all' ? 'bg-white text-[#2D3E6F] shadow' : 'text-slate-500 hover:text-slate-700'
                        }`}
                        data-testid="mode-all"
                      >
                        <Users className="w-3.5 h-3.5" />
                        Todos
                      </button>
                    </div>
                  </div>

                  {mode === 'single' && (
                    <div>
                      <Label className="text-sm">Comercial</Label>
                      <Select value={singleUserId} onValueChange={setSingleUserId}>
                        <SelectTrigger data-testid="single-user-select"><SelectValue placeholder="Escolher comercial..." /></SelectTrigger>
                        <SelectContent>
                          {filteredUsers.map(u => (
                            <SelectItem key={u.id} value={u.id}>
                              {u.name} {u.id === user?.id ? '(eu)' : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-slate-400 mt-1">Todas as leads do ficheiro vão para este comercial.</p>
                    </div>
                  )}

                  {mode === 'pool' && (
                    <div>
                      <Label className="text-sm">Distribuir igualmente entre:</Label>
                      <div className="space-y-1 mt-2 p-3 bg-slate-50 rounded-lg max-h-72 overflow-y-auto">
                        {filteredUsers.length === 0 ? (
                          <p className="text-sm text-slate-400">Sem comerciais neste parceiro</p>
                        ) : filteredUsers.map(u => (
                          <label key={u.id} className="flex items-center gap-2.5 py-1.5 cursor-pointer hover:bg-white rounded px-2 transition-colors" data-testid={`pool-user-${u.id}`}>
                            <Checkbox
                              checked={poolUserIds.includes(u.id)}
                              onCheckedChange={() => togglePool(u.id)}
                            />
                            <span className="text-sm text-slate-700">{u.name}</span>
                            <span className="text-xs text-slate-400 ml-auto">{u.role}</span>
                          </label>
                        ))}
                      </div>
                      <p className="text-xs text-slate-400 mt-2">
                        {poolUserIds.length === 0
                          ? 'Selecione pelo menos 2 comerciais.'
                          : poolUserIds.length === 1
                            ? '⚠️ Selecione mais 1 — distribuição precisa de pelo menos 2.'
                            : `✅ ${poolUserIds.length} comerciais selecionados — leads serão repartidas igualmente em round-robin.`
                        }
                      </p>
                    </div>
                  )}

                  {mode === 'all' && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3" data-testid="all-mode-info">
                      <p className="text-sm text-emerald-800 font-medium flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        Todos os comerciais activos deste parceiro
                      </p>
                      <p className="text-xs text-emerald-700 mt-1">
                        {filteredUsers.length === 0
                          ? 'Sem comerciais activos neste parceiro.'
                          : `Round-robin entre ${filteredUsers.length} comercia${filteredUsers.length === 1 ? 'l' : 'is'}: ${filteredUsers.slice(0, 5).map(u => u.name.split(' ')[0]).join(', ')}${filteredUsers.length > 5 ? ` +${filteredUsers.length - 5}` : ''}`
                        }
                      </p>
                    </div>
                  )}
                </>
              )}
              </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 3: Upload */}
        <Card className="rounded-2xl border-slate-100">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#2D3E6F] text-white text-xs font-bold">{isManager ? 3 : 2}</span>
              Carrega o ficheiro preenchido
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <label className="flex items-center justify-center gap-3 p-6 border-2 border-dashed border-slate-200 rounded-xl cursor-pointer hover:border-[#2D3E6F]/40 hover:bg-slate-50 transition-colors">
              <input
                type="file"
                data-max-mb="0"
                accept=".xlsx,.xls,.csv"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="file-input"
              />
              <FileSpreadsheet className="w-8 h-8 text-slate-400" />
              <div className="text-sm">
                {file ? <p className="font-semibold text-slate-700">{file.name}</p> : (
                  <>
                    <p className="font-semibold text-slate-700">Clica para selecionar</p>
                    <p className="text-slate-400 text-xs">Excel (.xlsx, .xls) ou CSV</p>
                  </>
                )}
              </div>
            </label>
            <div>
              <Label className="text-sm">Setor / Serviço destas leads <span className="text-rose-500">*</span></Label>
              <Select value={defaultSector} onValueChange={setDefaultSector}>
                <SelectTrigger data-testid="import-sector-select" className={!defaultSector ? 'border-rose-300' : ''}>
                  <SelectValue placeholder="Selecionar setor..." />
                </SelectTrigger>
                <SelectContent>
                  {["Luz e Gas", "Solar", "Carregamento Eletrico", "Telecomunicacoes", "Alarmes", "Outro"].map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-400 mt-1">Aplicado a todas as leads do ficheiro (exceto às que tiverem coluna "Serviço" preenchida).</p>
            </div>
            <Button
              onClick={handleImport}
              disabled={!file || !defaultSector || importing}
              className="w-full bg-[#2D3E6F] hover:bg-[#1a2a4a] gap-2 rounded-full"
              data-testid="import-btn"
            >
              {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {importing ? 'A importar...' : 'Importar Leads'}
            </Button>
          </CardContent>
        </Card>

        {/* Result */}
        {result && (
          <Card className="rounded-2xl border-slate-100" data-testid="import-result">
            <CardHeader><CardTitle className="text-base">Resultado</CardTitle></CardHeader>
            <CardContent className="space-y-4 text-sm">
              <div className="grid grid-cols-3 gap-3">
                <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200 text-center">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-emerald-700">{result.inserted}</p>
                  <p className="text-xs text-emerald-600">Importadas</p>
                </div>
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200 text-center">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-amber-700">{result.skipped}</p>
                  <p className="text-xs text-amber-600">Ignoradas</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 text-center">
                  <FileSpreadsheet className="w-5 h-5 text-slate-500 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-slate-700">{result.total_rows}</p>
                  <p className="text-xs text-slate-600">Total</p>
                </div>
              </div>

              {result.distribution && result.distribution.length > 0 && (
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1.5">
                    {result.mode === 'round_robin' ? <Users className="w-3.5 h-3.5" /> : <User className="w-3.5 h-3.5" />}
                    Distribuição:
                  </p>
                  <div className="space-y-1">
                    {result.distribution.map(d => (
                      <div key={d.user_id} className="flex items-center justify-between text-xs">
                        <span className="text-slate-600">{d.name}</span>
                        <span className="font-semibold text-[#2D3E6F]">{d.count} lead{d.count === 1 ? '' : 's'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {result.errors && result.errors.length > 0 && (
                <div className="p-3 bg-rose-50 rounded-lg border border-rose-200 space-y-1">
                  <p className="text-xs font-semibold text-rose-700 flex items-center gap-1"><XCircle className="w-3 h-3" /> Erros</p>
                  {result.errors.map((e, i) => <p key={i} className="text-xs text-rose-600">{e}</p>)}
                </div>
              )}

              {result.inserted > 0 && (
                <Button onClick={() => navigate('/leads/pipeline')} variant="outline" className="w-full rounded-full">
                  Ver no Pipeline →
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Importações Recentes */}
        {isManager && batches.length > 0 && (
          <Card className="rounded-2xl border-slate-100" data-testid="batches-card">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <History className="w-4 h-4 text-[#2D3E6F]" /> Importações Recentes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {batches.map((b) => {
                  const d = new Date(b.batch_id);
                  const dateStr = d.toLocaleString('pt-PT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
                  const topUser = b.by_user?.[0];
                  return (
                    <div key={b.batch_id} className="border border-slate-200 rounded-lg p-3 hover:bg-slate-50 transition" data-testid={`batch-${b.batch_id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-slate-800 text-sm">{dateStr}</span>
                            <span className="text-xs bg-slate-100 px-2 py-0.5 rounded">{b.total} leads</span>
                            {b.untouched_count === b.total ? (
                              <span className="text-[11px] bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded border border-emerald-200">✓ Por trabalhar</span>
                            ) : (
                              <span className="text-[11px] bg-amber-50 text-amber-700 px-2 py-0.5 rounded border border-amber-200">⚠️ {b.total - b.untouched_count}/{b.total} já trabalhadas</span>
                            )}
                          </div>
                          <div className="text-xs text-slate-500 mt-1">
                            Importado por <b>{b.created_by_name}</b> · {b.by_user.length === 1 ? `100% para ${topUser?.name}` : `${b.by_user.length} comerciais (${topUser?.name}, ...)`}
                          </div>
                          <div className="text-[11px] text-slate-400 mt-0.5 truncate">Ex: {b.sample_names.join(' · ')}</div>
                        </div>
                        <div className="flex gap-1.5 flex-shrink-0">
                          <Button size="sm" variant="outline" onClick={() => { setBatchAction({ type: 'reassign', batch: b }); setReassignTargetPartner(''); setReassignTargetUser(''); setUnassignBatch(false); }} data-testid={`reassign-batch-btn`}>
                            <ArrowRightLeft className="w-3.5 h-3.5 mr-1" /> Reatribuir
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setBatchAction({ type: 'delete', batch: b, force: false })} className="border-rose-200 text-rose-700 hover:bg-rose-50" data-testid={`delete-batch-btn`}>
                            <Trash2 className="w-3.5 h-3.5 mr-1" /> Apagar
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Dialog para acções em lote */}
      <Dialog open={!!batchAction} onOpenChange={(o) => { if (!o) { setBatchAction(null); setUnassignBatch(false); } }}>
        <DialogContent className="sm:max-w-lg" data-testid="batch-action-dialog">
          {batchAction?.type === 'delete' && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-rose-700"><Trash2 className="w-5 h-5" /> Apagar Importação</DialogTitle>
                <DialogDescription>
                  Vais apagar <b>{batchAction.batch.total} leads</b> importadas em <b>{new Date(batchAction.batch.batch_id).toLocaleString('pt-PT')}</b>.
                </DialogDescription>
              </DialogHeader>
              <div className="bg-rose-50 border border-rose-200 rounded p-3 text-sm text-rose-800">
                {batchAction.batch.untouched_count === batchAction.batch.total ? (
                  <>✓ Todas as leads estão por trabalhar — segura a apagar.</>
                ) : (
                  <>
                    ⚠️ <b>{batchAction.batch.total - batchAction.batch.untouched_count}</b> destas leads já foram trabalhadas (contactadas, reagendadas, ou convertidas).
                    <label className="flex items-center gap-2 mt-2 cursor-pointer">
                      <Checkbox checked={!!batchAction.force} onCheckedChange={(v) => setBatchAction({ ...batchAction, force: !!v })} />
                      <span className="text-xs">Apagar mesmo assim (perde-se o histórico)</span>
                    </label>
                  </>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setBatchAction(null)}>Cancelar</Button>
                <Button onClick={confirmBatchAction} disabled={batchActionLoading} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="confirm-delete-batch-btn">
                  {batchActionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Apagar {batchAction.batch.total} leads
                </Button>
              </DialogFooter>
            </>
          )}
          {batchAction?.type === 'reassign' && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><ArrowRightLeft className="w-5 h-5" /> Reatribuir Importação</DialogTitle>
                <DialogDescription>
                  {batchAction.batch.total} leads importadas em {new Date(batchAction.batch.batch_id).toLocaleString('pt-PT')} — escolhe o novo destino.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                {/* Opção: pôr de volta por atribuir */}
                <label
                  className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-colors ${
                    unassignBatch ? 'border-amber-500 bg-amber-50' : 'border-slate-200 hover:bg-slate-50'
                  }`}
                  data-testid="reassign-unassign-toggle"
                >
                  <Checkbox checked={unassignBatch} onCheckedChange={(v) => setUnassignBatch(!!v)} className="mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-slate-800">Deixar por atribuir (pôr no pool)</p>
                    <p className="text-xs text-slate-500 mt-0.5">Remove o comercial de todas estas leads para as distribuíres depois.</p>
                  </div>
                </label>

                {!unassignBatch && (
                <>
                <div>
                  <Label className="text-xs">Parceiro destino</Label>
                  <Select value={reassignTargetPartner} onValueChange={(v) => { setReassignTargetPartner(v); setReassignTargetUser(''); setBatchAction({ ...batchAction, partner_id: v === '__all__' ? '' : v }); }}>
                    <SelectTrigger data-testid="reassign-partner-select"><SelectValue placeholder="Escolher parceiro..." /></SelectTrigger>
                    <SelectContent>
                      {partners.map(p => (
                        <SelectItem key={p.id} value={p.id} disabled={p.comerciais_count === 0}>
                          {p.name} · {p.comerciais_count} comerciais
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {reassignTargetPartner && (
                  <div>
                    <Label className="text-xs">Comercial específico (deixa vazio para distribuir round-robin pelo parceiro)</Label>
                    <Select value={reassignTargetUser || '__all__'} onValueChange={(v) => { if (v === '__all__') { setReassignTargetUser(''); setReassignTargetPartner('__all__'); setBatchAction({ ...batchAction, partner_id: reassignTargetPartner }); } else { setReassignTargetUser(v); setReassignTargetPartner(reassignTargetPartner); } }}>
                      <SelectTrigger data-testid="reassign-user-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__all__">✨ Distribuir por todo o parceiro (round-robin)</SelectItem>
                        {users.filter(u => u.partner_id === reassignTargetPartner).map(u => (
                          <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                </>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setBatchAction(null)}>Cancelar</Button>
                <Button onClick={confirmBatchAction} disabled={batchActionLoading || (!unassignBatch && !reassignTargetUser && reassignTargetPartner !== '__all__')} className={unassignBatch ? 'bg-amber-600 hover:bg-amber-700 text-white' : 'bg-indigo-600 hover:bg-indigo-700 text-white'} data-testid="confirm-reassign-batch-btn">
                  {batchActionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} {unassignBatch ? `Pôr ${batchAction.batch.total} por atribuir` : `Reatribuir ${batchAction.batch.total} leads`}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default LeadImportPage;
